import { Box, Flex } from '@chakra-ui/react';
import { BudgetBarChart } from 'Deer/components/Compensation/BudgetBarChart';

import { minifyNumber } from 'constants/numberMinifyHelper';

import { Avatar } from 'uiToolkitV1/Avatar';
import { TagColored } from 'uiToolkitV1/Tag';
import { TextBodyMedium, TextLabel, TextLabelSmall } from 'uiToolkitV1/Typography';

export const ApproverCellRenderer = ({
  value: { displayName, profilePhoto, email, mainRole },
}: {
  value: { displayName: string; profilePhoto: string; email: string; mainRole: string };
}) => (
  <Flex alignItems="center" gap="8px" h="100%">
    <Avatar name={displayName} src={profilePhoto} size={24} />
    <Flex flexDirection="column">
      <TextBodyMedium>{displayName}</TextBodyMedium>
      <TextLabel color="text.g2">{email}</TextLabel>
      <TextLabelSmall color="text.g2">{mainRole}</TextLabelSmall>
    </Flex>
  </Flex>
);

export const CompensationCycleCellRenderer = ({ value }: { value: string }) => (
  <Flex align="center" h="100%">
    <TextBodyMedium>{value}</TextBodyMedium>
  </Flex>
);

export const BUDGET_STATUS_CONFIG = {
  WITHIN_BUDGET: {
    label: 'Within budget',
    color: 'green',
  },
  EXCEEDED: {
    label: 'Exceeded',
    color: 'red',
  },
} as const;

export const BudgetStatusCellRenderer = ({ value }: { value: keyof typeof BUDGET_STATUS_CONFIG }) => (
  <Flex align="center" h="100%">
    <TagColored color={BUDGET_STATUS_CONFIG[value].color}>{BUDGET_STATUS_CONFIG[value].label}</TagColored>
  </Flex>
);

const formatCurrency = (value: number) => {
  if (value >= 1000) {
    return minifyNumber(value, {
      precision: 1,
      lowercase: false,
      space: false,
      units: ['', 'K', 'M', 'B', 'T', 'P', 'E'],
      locales: 'en-US',
    });
  }

  return value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

export const BudgetBarCellRenderer = ({
  value,
}: {
  value: {
    allocatedBudget: { value: number; currency: string; symbol: string };
    additionalBudget: { value: number; currency: string; symbol: string };
    totalBudget: { value: number; currency: string; symbol: string };
    targetBudget: { value: number; currency: string; symbol: string };
  };
}) => (
  <Flex flexDir="column" justify="center" h="100%" gap="5px">
    <TextLabelSmall color="text.g2">
      Target budget:{' '}
      <Box as="span" color="functional.C62C02" fontWeight="590">
        {formatCurrency(value.targetBudget.value)}
      </Box>
    </TextLabelSmall>
    <BudgetBarChart
      totalBudget={value.totalBudget.value}
      currentComp={value.allocatedBudget.value}
      proposedComp={value.additionalBudget.value}
      targetBudget={value.targetBudget.value}
      showLegend={false}
      showAxisLabels={false}
      showPriceLabel={false}
      wrapperProps={{
        h: 'auto',
        bg: 'transparent',
      }}
      chartContainerProps={{ h: '20px' }}
    />
    <TextLabelSmall color="text.g2">
      Current :
      <Box as="span" color="functional.44812F" fontWeight="590">
        {formatCurrency(value.allocatedBudget.value)}
      </Box>
      • New :
      <Box as="span" color="functional.B96400" fontWeight="590">
        {formatCurrency(value.additionalBudget.value)}
      </Box>
      • Unused :
      <Box as="span" fontWeight="590">
        {formatCurrency(value.totalBudget.value - value.allocatedBudget.value - value.additionalBudget.value)}
      </Box>
    </TextLabelSmall>
  </Flex>
);
